import {dataset, classList, dispatch} from 'nuffjs';
import {onImageLoad} from '../utils/on-image-load';

/**
 * @property {string} loadedClass=loaded
 * @property {string} loadEvent=img:load
 * @memberof LazyImg
 */
const conf = {
	loadedClass: 'loaded',
	loadEvent: 'img:load',
};

/**
 * @classdesc Implements the lazy-loading strategy for the images
 *
 * @emits img:load
 *
 * @class LazyImg
 * @extends {HTMLImageElement}
 * @implements {LoadableInterface}
 *
 * @version 1.0.0
 * @path
 */
class LazyImg extends HTMLImageElement {
	static conf = conf;

	/**
	 * @property {string} autoload=lazy control the autoloading. true = immediate loading, false =
	 * load only when the method is called programmatically, lazy = load controlled by an
	 * intersection observer
	 *
	 * @static
	 */
	static defaultOpts = {
		autoload: 'lazy',
	};

	/**
	 * Initialization. It triggers the load when the data-autoload attribute is set to true
	 *
	 */
	firstConnectedCallback() {
		this.opts = dataset(this).get(this.constructor.defaultOpts);
		if (!this.asyncLoading && this.opts.autoload === true) {
			this.load();
		}
	}

	/**
	 * Do the actual loading by replacing data attributes data-srcset and data-sizes with the proper
	 * attributes, both in the <img> tag and the <source> ones when it is inside a <picture>
	 *
	 * @emits img:load
	 *
	 * @returns {Promise} resolved when the image is loaded
	 */
	load() {
		if (!this.asyncLoading) {
			this.asyncLoading = new Promise(async (resolve) => {
				const isPicture = this.parentNode instanceof HTMLPictureElement;
				const nodes = [this].concat(
					isPicture ? [...this.parentNode.querySelectorAll('source')] : []
				);
				for (const node of nodes) {
					const data = dataset(node);
					for (const attr of ['sizes', 'srcset']) {
						const value = data.get(attr, null);
						if (value) {
							node.setAttribute(attr, value);
							data.remove(attr);
						}
					}
				}
				const loaded = await onImageLoad(this);
				classList(this).add(conf.loadedClass);
				if (isPicture) {
					classList(this.parentNode).add(conf.loadedClass);
				}
				resolve(loaded);
				dispatch(this, conf.loadEvent, {bubbles: true, late: true});
			});
		}
		return this.asyncLoading;
	}
}

export {LazyImg, conf as lazyImgConf};
